import { useRef, useState } from 'react';
import { Loader2, UploadIcon, UserIcon } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage, Button, toast } from '@opengoal/ui';
import { useTranslation } from 'react-i18next';
import { PresignUploadDtoScope } from '@/api/generated/model';
import { useFileUploadMutation } from '../hooks/usePlayers';

/** Mirrors the backend's `player-photo` scope; enforced there, echoed here. */
const ACCEPTED = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_BYTES = 5 * 1024 * 1024;

export interface PlayerPhotoUploadProps {
  /** The storage key, or undefined. */
  value?: string | undefined;
  onChange?: (key: string | undefined) => void;
}

/**
 * Uploads a player photo and yields the storage **key**, not a URL.
 *
 * Shaped as a controlled input so a form field can drive it: `value` is the key
 * the form holds, and `onChange` hands back the new one. The file goes straight
 * from the browser to S3 via a presigned URL, so nothing here streams bytes
 * through our API.
 *
 * The preview is a local object URL rather than the CDN URL, because the
 * uploaded object is not readable until the player is saved and the response
 * comes back with a resolved `photoUrl` — showing a CDN link immediately would
 * render a broken image.
 */
export function PlayerPhotoUpload({ value, onChange }: PlayerPhotoUploadProps) {
  const { t } = useTranslation();
  const upload = useFileUploadMutation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const pick = (file: File | undefined) => {
    if (!file) return;
    if (!ACCEPTED.includes(file.type)) {
      toast.error(t('players.photo.wrongType'));
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error(t('players.photo.tooLarge', { mb: MAX_BYTES / 1024 / 1024 }));
      return;
    }

    upload.mutate(
      { file, scope: PresignUploadDtoScope['player-photo'] },
      {
        onSuccess: (key) => {
          // Revoked before being replaced so a form the user edits repeatedly
          // does not leak one object URL per attempt.
          if (previewUrl) URL.revokeObjectURL(previewUrl);
          setPreviewUrl(URL.createObjectURL(file));
          onChange?.(key);
        },
        onError: () => {
          toast.error(t('players.photo.failed'));
        },
      },
    );
  };

  const clear = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    onChange?.(undefined);
  };

  return (
    <div className="flex items-center gap-4">
      <Avatar className="size-16 ring-1 ring-border">
        {previewUrl ? <AvatarImage src={previewUrl} alt="" /> : null}
        <AvatarFallback>
          {upload.isPending ? (
            <Loader2 className="size-5 animate-spin" />
          ) : (
            <UserIcon className="size-6" />
          )}
        </AvatarFallback>
      </Avatar>
      <div className="grid justify-items-start gap-1">
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          accept={ACCEPTED.join(',')}
          onChange={(event) => {
            pick(event.target.files?.[0]);
            event.target.value = '';
          }}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          loading={upload.isPending}
          onClick={() => inputRef.current?.click()}
        >
          <UploadIcon />
          {value ? t('players.photo.replace') : t('players.photo.upload')}
        </Button>
        {value ? (
          <Button
            type="button"
            variant="link"
            size="xs"
            className="px-0 text-muted-foreground"
            onClick={clear}
          >
            {t('players.photo.remove')}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
